import { useEffect } from 'react';
import { ShoppingBag, Flag, CreditCard, Shield, Star, Users, Heart, Award } from 'lucide-react';

const Store = () => {
  useEffect(() => {
    document.title = 'Store | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  const products = [
    {
      id: 1,
      name: "Te Hake (Māori Flag)",
      price: 45,
      image: "🏴",
      description: "Official Māori flag representing tino rangatiratanga and Māori identity",
      features: ["High-quality polyester", "Weather resistant", "Multiple sizes available", "Made in Aotearoa"]
    },
    {
      id: 2,
      name: "Hapū Identity Card",
      price: 25,
      image: "🪪",
      description: "Official hapū membership card based on He Whakaputanga customary rights",
      features: ["Customary rights summary", "Hapū-specific design", "Durable material", "Official recognition"]
    },
    {
      id: 3,
      name: "Te Tairawhiti Hapū Card",
      price: 30,
      image: "🆔",
      description: "Special edition card for Te Tairawhiti hapū with regional significance",
      features: ["Regional hapū design", "Customary rights outline", "Cultural protocols", "Collector edition"]
    },
    {
      id: 4,
      name: "Tangata Whenua Rights Card",
      price: 30,
      image: "⚖️",
      description: "Informational card outlining your rights as tangata whenua",
      features: ["Legal reference guide", "Emergency contact numbers", "Rights summary", "Waterproof material"]
    },
    {
      id: 5,
      name: "Traditional Knowledge Bundle",
      price: 65,
      image: "📚",
      description: "Comprehensive guide to traditional Māori practices and knowledge",
      features: ["200+ page handbook", "Cultural protocols guide", "Seasonal calendar", "Pronunciation guide"]
    },
    {
      id: 6,
      name: "Whakapapa Research Kit",
      price: 85,
      image: "🌳",
      description: "Complete genealogy research toolkit for discovering your whakapapa",
      features: ["Research templates", "Interview guides", "Documentation forms", "Online access codes"]
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Hapū Store
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Supporting hapū identity and customary rights with authentic materials and resources
          </p>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Shield className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-semibold text-gray-800">Secure Payments</h3>
              <p className="text-gray-600 text-sm">Protected transactions</p>
            </div>
            <div className="flex flex-col items-center">
              <Award className="h-8 w-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-800">Authentic Products</h3>
              <p className="text-gray-600 text-sm">Culturally appropriate items</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="h-8 w-8 text-purple-600 mb-2" />
              <h3 className="font-semibold text-gray-800">Community Owned</h3>
              <p className="text-gray-600 text-sm">Māori-owned business</p>
            </div>
            <div className="flex flex-col items-center">
              <Heart className="h-8 w-8 text-ocean-teal mb-2" />
              <h3 className="font-semibold text-gray-800">Proceeds Support</h3>
              <p className="text-gray-600 text-sm">Cultural education programs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Featured Products</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div key={product.id} className="card hover:shadow-xl transition-all duration-300 group">
                <div className="text-center mb-4">
                  <span className="text-6xl mb-4 block">{product.image}</span>
                  <h3 className="text-xl font-semibold text-gray-800 group-hover:text-ocean-teal transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-2xl font-bold text-ocean-teal mt-2">${product.price} NZD</p>
                </div>
                
                <p className="text-gray-600 mb-4">{product.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Features:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Star className="h-3 w-3 text-yellow-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button className="w-full btn-primary group-hover:scale-105 transition-transform">
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Offers */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Special Packages</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10 border-2 border-ocean-blue border-opacity-30">
              <div className="text-center mb-4">
                <Flag className="h-12 w-12 text-ocean-teal mx-auto mb-3" />
                <h3 className="text-xl font-semibold text-ocean-deep">Hapū Rights Pack</h3>
                <p className="text-3xl font-bold text-ocean-teal mt-2">$90 <span className="text-lg text-gray-600">Save $15</span></p>
              </div>
              <p className="text-gray-700 mb-4">Essential items for understanding your hapū customary rights</p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Te Hake Flag (based on He Whakaputanga)</li>
                <li>• Hapū Identity Card</li>
                <li>• Tangata Whenua Rights Card</li>
                <li>• He Whakaputanga education booklet</li>
              </ul>
              <button className="w-full btn-primary">
                Get Started Package
              </button>
            </div>

            <div className="card bg-purple-50 border-2 border-purple-200">
              <div className="text-center mb-4">
                <Users className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                <h3 className="text-xl font-semibold text-purple-700">Whakapapa Discovery Pack</h3>
                <p className="text-3xl font-bold text-purple-600 mt-2">$135 <span className="text-lg text-gray-600">Save $25</span></p>
              </div>
              <p className="text-gray-700 mb-4">Complete genealogy research and documentation kit</p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Whakapapa Research Kit</li>
                <li>• Traditional Knowledge Bundle</li>
                <li>• Iwi & Hapū Identity Cards</li>
                <li>• 1-hour consultation session</li>
              </ul>
              <button className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold">
                Discover Your Roots
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Payment & Shipping */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Payment & Delivery</h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="card text-center">
              <CreditCard className="h-10 w-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Secure Payment</h3>
              <p className="text-gray-600 text-sm mb-3">We accept all major credit cards, PayPal, and bank transfers</p>
              <ul className="text-xs text-gray-500">
                <li>• SSL encrypted checkout</li>
                <li>• No stored payment details</li>
                <li>• Fraud protection</li>
              </ul>
            </div>

            <div className="card text-center">
              <ShoppingBag className="h-10 w-10 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Fast Delivery</h3>
              <p className="text-gray-600 text-sm mb-3">Free shipping within New Zealand on orders over $50</p>
              <ul className="text-xs text-gray-500">
                <li>• 3-5 business days delivery</li>
                <li>• Express options available</li>
                <li>• Tracking included</li>
              </ul>
            </div>

            <div className="card text-center">
              <Heart className="h-10 w-10 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Community Support</h3>
              <p className="text-gray-600 text-sm mb-3">Every purchase supports Māori education and cultural programs</p>
              <ul className="text-xs text-gray-500">
                <li>• Cultural education funding</li>
                <li>• Community development</li>
                <li>• Scholarship programs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-ocean-teal text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Support Your Cultural Journey
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Every purchase helps strengthen Māori communities and supports the preservation 
            of cultural knowledge for future generations.
          </p>
          <button className="bg-white text-ocean-teal px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg">
            Start Shopping Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default Store;
